import React, { useEffect, useMemo, useRef } from 'react';

import { MODAL_CONFIG } from '@atlassian/edition-benefits-modal/constants';
import { editionBenefitsModalEntryPoint } from '@atlassian/edition-benefits-modal/edition-benefits-modal-entrypoint';
import { useFirePostOfficeTrigger } from '@atlassian/post-office-context';

import { useEditionAwareness } from '../../../services/edition-awareness-context';
import { ModalEntryPoint } from '../shared/modal-entry-point';

import { DIALOG_CONFIG } from './dialog-config';
import { useVariantId } from './use-variant-id';

export function ExploreEditionBenefitsModal(): React.JSX.Element {
	const {
		product,
		cloudId,
		edition,
		currentRoute,
		userEmail,
		shouldAutoOpen,
		onAutoOpen,
		lastKnownTrial,
	} = useEditionAwareness();

	const isStorageLimitBreached = lastKnownTrial?.trialTrigger === 'AUTO_UPGRADE_STORAGE_LIMIT';

	const variantId = useVariantId({ product, currentRoute, isStorageLimitBreached });
	const initialOpenModal = useRef(shouldAutoOpen);

	const entryPointParams = useMemo(() => ({ cloudId, product }), [cloudId, product]);

	const entryPointProps = useMemo(
		() => ({
			variantId,
			touchpointId: 'edition-awareness-explore-modal' as const,
			edition,
			email: userEmail ?? undefined,
		}),
		[edition, variantId, userEmail],
	);

	const { fireTrigger: firePopupTrigger } = useFirePostOfficeTrigger({
		triggerId: 'jsm-edition-awareness-button-popup',
	});
	const { fireTrigger: fireModalTrigger } = useFirePostOfficeTrigger({
		triggerId: 'edition-benefits-modal',
	});

	// Clear the query param once we've consumed it, so re-mounts don't re-open
	useEffect(() => {
		if (shouldAutoOpen) {
			onAutoOpen?.();
		}
	}, [shouldAutoOpen, onAutoOpen]);

	useEffect(() => {
		if (initialOpenModal.current) {
			fireModalTrigger({ isActive: true });
		} else {
			firePopupTrigger({ isActive: true });
		}

		return () => {
			firePopupTrigger({ isActive: false });
			fireModalTrigger({ isActive: false });
		};
	}, [firePopupTrigger, fireModalTrigger]);

	return (
		<ModalEntryPoint
			entryPoint={editionBenefitsModalEntryPoint}
			entryPointParams={entryPointParams}
			entryPointProps={entryPointProps}
			dialogConfig={DIALOG_CONFIG}
			modalProps={MODAL_CONFIG}
		/>
	);
}
